import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { campaignService } from '../services/campaignService';
import { formatCompactNumber } from '../utils/format';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Skeleton from '../components/ui/Skeleton';
import EventBus from '../utils/EventBus';
import styles from './CampaignDetail.module.css';

const FUNNEL_STEPS = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'converted', label: 'Converted' },
];
const FUNNEL_COLORS = ['#686868', '#4878B0', '#C09848', '#8878C0', '#4F9B6F'];

class CampaignDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, campaign: null, analytics: null, confirmCancel: false, acting: false };
    this.load = this.load.bind(this);
    this.act = this.act.bind(this);
  }

  componentDidMount() {
    this.load();
  }

  load() {
    const { id } = this.props;
    this.setState({ loading: true });
    Promise.all([campaignService.getById(id), campaignService.analytics(id)])
      .then(([campaign, analytics]) => this.setState({ loading: false, campaign, analytics }))
      .catch((err) => {
        this.setState({ loading: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to load campaign.' });
      });
  }

  act(action, successMessage) {
    this.setState({ acting: true });
    action()
      .then(() => {
        this.setState({ acting: false, confirmCancel: false });
        EventBus.emit('toast', { type: 'success', message: successMessage });
        this.load();
      })
      .catch((err) => {
        this.setState({ acting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Action failed.' });
      });
  }

  render() {
    const { navigate } = this.props;
    const { loading, campaign, analytics, confirmCancel, acting } = this.state;

    if (loading || !campaign) {
      return (
        <div className={styles.loadingWrap}>
          <Skeleton variant="text" width={240} height={20} />
          <Skeleton variant="rect" width="100%" height={200} style={{ marginTop: 16, borderRadius: 12 }} />
        </div>
      );
    }

    const funnel = analytics?.funnel || {};
    const maxFunnel = Math.max(1, funnel.sent || 0);
    const opensOverTime = (analytics?.opens_over_time || []).map((r) => ({
      hour: new Date(r.hour).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric' }),
      count: Number(r.count),
    }));
    const channelBreakdown = analytics?.channel_breakdown || [];
    const failedReasons = analytics?.failed_reasons || [];

    const canPause = ['scheduled', 'sending'].includes(campaign.status);
    const canResume = campaign.status === 'paused';
    const canCancel = !['completed', 'cancelled'].includes(campaign.status);
    const canEdit = ['draft', 'scheduled', 'paused'].includes(campaign.status);

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <Link to="/campaigns" className={styles.backLink}>← Campaigns</Link>
            <div className={styles.titleRow}>
              <div className={styles.pageTitle}>{campaign.name}</div>
              <Badge status={campaign.status} dot>{campaign.status}</Badge>
            </div>
            <div className={styles.pageSub}>{campaign.channel} · created {new Date(campaign.created_on).toLocaleDateString()}</div>
          </div>
          <div className={styles.actions}>
            {canEdit && (
              <Button variant="ghost" onClick={() => EventBus.emit('openWizard', { campaign })}>Edit</Button>
            )}
            <Button
              variant="ghost"
              disabled={acting}
              onClick={() => this.act(() => campaignService.duplicate(campaign.id).then((c) => navigate('/campaigns/' + c.id)), 'Campaign duplicated.')}
            >
              Duplicate
            </Button>
            {canPause && (
              <Button variant="ghost" disabled={acting} onClick={() => this.act(() => campaignService.pause(campaign.id), 'Campaign paused.')}>
                Pause
              </Button>
            )}
            {canResume && (
              <Button variant="ghost" disabled={acting} onClick={() => this.act(() => campaignService.resume(campaign.id), 'Campaign resumed.')}>
                Resume
              </Button>
            )}
            {canCancel && (
              <Button variant="danger" disabled={acting} onClick={() => this.setState({ confirmCancel: true })}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        <div className={styles.statRow}>
          {FUNNEL_STEPS.map((s) => (
            <div key={s.key} className={styles.statCard}>
              <div className={styles.statVal}>{formatCompactNumber(funnel[s.key] || 0)}</div>
              <div className={styles.statLbl}>{s.label}</div>
            </div>
          ))}
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Funnel</div>
          </div>
          <div className={styles.funnelWrap}>
            {FUNNEL_STEPS.map((s, i) => {
              const count = funnel[s.key] || 0;
              const pct = maxFunnel ? Math.round((count / maxFunnel) * 100) : 0;
              const col = FUNNEL_COLORS[i % FUNNEL_COLORS.length];
              return (
                <div key={s.key} className={styles.funnelRow}>
                  <div className={styles.funnelBar} style={{ width: pct + '%', background: col + '22', borderLeft: '3px solid ' + col }} />
                  <div className={styles.funnelLabel}>{s.label}</div>
                  <div className={styles.funnelCount} style={{ color: col }}>{formatCompactNumber(count)}</div>
                  <div className={styles.funnelPct}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.grid2}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Opens over time</div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={opensOverTime} margin={{ top: 8, right: 8, left: -20, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#C09848" fill="#C0984822" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Channel breakdown</div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={channelBreakdown} margin={{ top: 8, right: 8, left: -20, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                <XAxis dataKey="channel" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="sent" fill="#4878B0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="opened" fill="#C09848" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {failedReasons.length > 0 && (
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Why sends didn't go through</div>
            </div>
            <div className={styles.reasonList}>
              {failedReasons.map((r) => (
                <div key={r.reason} className={styles.reasonRow}>
                  <span className={styles.reasonName}>{r.reason}</span>
                  <span className={styles.reasonCount}>{formatCompactNumber(r.count)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <ConfirmDialog
          open={confirmCancel}
          title="Cancel campaign?"
          message={`"${campaign.name}" will stop sending immediately. This can't be undone.`}
          confirmLabel="Cancel campaign"
          loading={acting}
          onConfirm={() => this.act(() => campaignService.cancel(campaign.id), 'Campaign cancelled.')}
          onCancel={() => this.setState({ confirmCancel: false })}
        />
      </>
    );
  }
}

function CampaignDetailWithRouter() {
  const { id } = useParams();
  const navigate = useNavigate();
  return <CampaignDetail id={id} navigate={navigate} />;
}

export default CampaignDetailWithRouter;
